<div class="scan-insights">
  <p class="text-muted mb-3">
    Unmatched scan recovery, cart clears, and flagged events for CCTV cross-check.
    Default view is the <strong>last 30 days</strong>.
  </p>

  <!-- Date range -->
  <nb-card class="mb-4">
    <nb-card-body>
      <div class="row align-items-end g-3">
        <div class="col-md-3">
          <label class="form-label">From</label>
          <input type="date" class="form-control" [(ngModel)]="dateFrom" (change)="onFilterChange()">
        </div>
        <div class="col-md-3">
          <label class="form-label">To</label>
          <input type="date" class="form-control" [(ngModel)]="dateTo" (change)="onFilterChange()">
        </div>
        <div class="col-md-2">
          <button class="btn btn-outline-secondary w-100" (click)="resetFilters()">Last 30 days</button>
        </div>
        <div class="col-md-2">
          <button class="btn btn-primary w-100" (click)="loadInsights()">
            <nb-icon icon="refresh-outline"></nb-icon>
            Refresh
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>

  <div *ngIf="endpointUnavailable" class="alert alert-info mb-4">
    <strong>Backend not ready</strong> — {{ errorMessage }}
  </div>

  <div *ngIf="isLoading" class="text-center p-5">
    <nb-spinner size="large"></nb-spinner>
    <p class="mt-2 text-muted">Loading scan insights…</p>
  </div>

  <div *ngIf="!isLoading && errorMessage && !endpointUnavailable" class="text-center p-4">
    <p class="text-danger">{{ errorMessage }}</p>
    <button class="btn btn-outline-secondary btn-sm" (click)="loadInsights()">Try again</button>
  </div>

  <ng-container *ngIf="!isLoading && !errorMessage">
    <!-- Summary -->
    <div class="row mb-4 g-3">
      <div class="col-md-3">
        <nb-card class="summary-card h-100">
          <nb-card-body>
            <div class="summary-label">Unmatched scans</div>
            <div class="summary-value">{{ recoverySummary.totalUnmatched ?? '—' }}</div>
            <div class="summary-sub text-muted">
              {{ recoverySummary.recoveredWithin60s ?? 0 }} recovered within 60s
            </div>
          </nb-card-body>
        </nb-card>
      </div>
      <div class="col-md-3">
        <nb-card class="summary-card h-100">
          <nb-card-body>
            <div class="summary-label">Recovery rate</div>
            <div class="summary-value text-success">{{ formatPct(recoverySummary.recoveryPct) }}</div>
            <div class="summary-sub text-muted">Within 60 seconds</div>
          </nb-card-body>
        </nb-card>
      </div>
      <div class="col-md-3">
        <nb-card class="summary-card h-100">
          <nb-card-body>
            <div class="summary-label">Cart clears</div>
            <div class="summary-value">{{ clears.totalClears ?? '—' }}</div>
            <div class="summary-sub text-muted">{{ formatCurrency(clears.totalClearedDkk) }} cleared</div>
          </nb-card-body>
        </nb-card>
      </div>
      <div class="col-md-3">
        <nb-card class="summary-card h-100 summary-card-warning">
          <nb-card-body>
            <div class="summary-label">Not re-bought (15 min)</div>
            <div class="summary-value text-danger">{{ clears.notRepurchasedWithin15Min?.count ?? '—' }}</div>
            <div class="summary-sub text-danger fw-semibold">
              {{ formatCurrency(clears.notRepurchasedWithin15Min?.dkk) }}
            </div>
          </nb-card-body>
        </nb-card>
      </div>
    </div>

    <!-- Charts -->
    <div class="row mb-4 g-3">
      <div class="col-lg-6">
        <nb-card class="h-100">
          <nb-card-header>Daily unmatched scans</nb-card-header>
          <nb-card-body>
            <div *ngIf="dailyUnmatchedScans.length === 0" class="text-center text-muted p-4">
              No unmatched scan data for this period.
            </div>
            <div *ngIf="dailyUnmatchedScans.length > 0" class="chart-container">
              <canvas
                baseChart
                [data]="unmatchedChartData"
                [options]="unmatchedChartOptions"
                [type]="unmatchedChartType">
              </canvas>
            </div>
          </nb-card-body>
        </nb-card>
      </div>
      <div class="col-lg-6">
        <nb-card class="h-100">
          <nb-card-header>Daily cart clears</nb-card-header>
          <nb-card-body>
            <div *ngIf="dailyClears.length === 0" class="text-center text-muted p-4">
              No clear data for this period.
            </div>
            <div *ngIf="dailyClears.length > 0" class="chart-container">
              <canvas
                baseChart
                [data]="clearsChartData"
                [options]="clearsChartOptions"
                [type]="clearsChartType">
              </canvas>
            </div>
          </nb-card-body>
        </nb-card>
      </div>
    </div>

    <!-- Top unmatched codes -->
    <nb-card class="mb-4">
      <nb-card-header>Top unmatched codes</nb-card-header>
      <nb-card-body class="p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>Code</th>
                <th>Scan kind</th>
                <th class="text-end">Count</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of topUnmatchedCodes; trackBy: trackByCode">
                <td class="font-monospace">{{ row.code }}</td>
                <td>
                  <span class="badge" [ngClass]="getScanKindBadgeClass(row.scanKind)">
                    {{ getScanKindLabel(row.scanKind) }}
                  </span>
                </td>
                <td class="text-end fw-semibold">{{ row.count }}</td>
              </tr>
              <tr *ngIf="topUnmatchedCodes.length === 0">
                <td colspan="3" class="text-center text-muted p-4">No unmatched codes in this period.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </nb-card-body>
    </nb-card>

    <!-- Flagged clears -->
    <nb-card class="flagged-clears-card">
      <nb-card-header>
        <span class="text-danger fw-semibold">Flagged clears</span>
        <span class="text-muted ms-2 small">— possible theft pattern; cross-check CCTV timestamps</span>
      </nb-card-header>
      <nb-card-body class="p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0 flagged-table">
            <thead class="table-light">
              <tr>
                <th>Timestamp</th>
                <th>Terminal</th>
                <th>Customer</th>
                <th>Items</th>
                <th class="text-end">Total DKK</th>
                <th>Flag</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let clear of flaggedClears; trackBy: trackByEventId" class="flagged-row">
                <td class="text-nowrap timestamp-cell">
                  {{ clear.timestamp | date:'dd/MM/yyyy HH:mm:ss' }}
                </td>
                <td>
                  <span class="fw-semibold">{{ clear.terminalName || clear.terminalId || '—' }}</span>
                </td>
                <td class="customer-cell">
                  <span class="customer-name">{{ getCustomerLabel(clear) }}</span>
                </td>
                <td class="items-cell">{{ formatItems(clear.items) }}</td>
                <td class="text-end total-cell">{{ formatCurrency(clear.totalDkk) }}</td>
                <td>
                  <span *ngIf="clear.notPaidWithin15Min" class="badge bg-danger">Not paid 15 min</span>
                </td>
              </tr>
              <tr *ngIf="flaggedClears.length === 0">
                <td colspan="6" class="text-center text-muted p-4">No flagged clears in this period.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </nb-card-body>
    </nb-card>
  </ng-container>
</div>
